/**
 * DIAGNOSTIC GAME — Question Bank Index
 *
 * STRICT RULE: Completely separate from numerology data.
 * Each base number (1-9) has its own set of 12 questions, stored in its own
 * module under /data/questions/baseN.js. This index assembles them into
 * DIAGNOSTIC_QUESTIONS and exposes the shared helpers.
 *
 * Grammar rules for the question dataset:
 *   - Quiz options (a/b/c): 1st person singular (Aš), no gendered suffixes
 *   - Results/feedback: 2nd person singular (Tu)
 *
 * Structure:
 *   DIAGNOSTIC_QUESTIONS[baseNumber] → array of 12 question objects
 *   Each question: { id, sphere, question, choices: { a, b, c } }
 *
 * Spheres: bendra, karjera, santykiai, finansai, savirealizacija
 */

import base1 from '@/data/questions/base1';
import base2 from '@/data/questions/base2';
import base3 from '@/data/questions/base3';
import base4 from '@/data/questions/base4';
import base5 from '@/data/questions/base5';
import base6 from '@/data/questions/base6';
import base7 from '@/data/questions/base7';
import base8 from '@/data/questions/base8';
import base9 from '@/data/questions/base9';

export const SPHERE_LABELS = {
  bendra: 'Bendra',
  karjera: 'Karjera',
  santykiai: 'Santykiai',
  finansai: 'Finansai',
  savirealizacija: 'Savirealizacija',
};

export const DIAGNOSTIC_QUESTIONS = {
  1: base1,
  2: base2,
  3: base3,
  4: base4,
  5: base5,
  6: base6,
  7: base7,
  8: base8,
  9: base9,
};

/**
 * Check if a base number has a quiz available.
 */
export function hasQuiz(baseNumber) {
  return Boolean(DIAGNOSTIC_QUESTIONS[baseNumber]?.length);
}

/**
 * Calculate quiz results from an answers object.
 * @param {Object} answers - { q1: 'a', q2: 'b', ... }
 * @param {number} totalQuestions - total number of questions (12)
 * @returns {{ a: number, b: number, c: number, aPercent: number, bPercent: number, cPercent: number }}
 */
export function calculateDiagnosticScore(answers, totalQuestions = 12) {
  let counts = { a: 0, b: 0, c: 0 };
  Object.values(answers).forEach((val) => {
    if (counts[val] !== undefined) counts[val]++;
  });
  const answered = counts.a + counts.b + counts.c;
  const total = answered || totalQuestions;
  return {
    a: counts.a,
    b: counts.b,
    c: counts.c,
    aPercent: Math.round((counts.a / total) * 100),
    bPercent: Math.round((counts.b / total) * 100),
    cPercent: 100 - Math.round((counts.a / total) * 100) - Math.round((counts.b / total) * 100),
  };
}
